import { useState, useRef, useEffect } from 'react';
import { downloadCSV, downloadJSON, copyToClipboard, generateFilename, dataToCSV } from '../../lib/export-utils';

interface ExportMenuProps {
  data: Record<string, unknown>;
  title?: string;
}

export default function ExportMenu({ data, title = 'data' }: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Extract array data from the data object (items, things, etc.)
  const getExportableData = (): Record<string, unknown>[] => {
    // Check common keys for array data
    for (const key of ['items', 'things', 'holders', 'customers', 'data', 'rows']) {
      const value = data[key];
      if (Array.isArray(value)) {
        return value as Record<string, unknown>[];
      }
    }
    // If data itself is an array
    if (Array.isArray(data)) {
      return data as unknown as Record<string, unknown>[];
    }
    // Fallback: wrap single object
    return [data];
  };

  const exportableData = getExportableData();
  const hasData = exportableData.length > 0;

  const handleCSVExport = () => {
    const filename = generateFilename(title.toLowerCase().replace(/\s+/g, '-'), 'csv');
    downloadCSV(exportableData, filename);
    setIsOpen(false);
  };

  const handleJSONExport = () => {
    const filename = generateFilename(title.toLowerCase().replace(/\s+/g, '-'), 'json');
    downloadJSON(exportableData, filename);
    setIsOpen(false);
  };

  const handleCopyCSV = async () => {
    const csv = dataToCSV(exportableData);
    const success = await copyToClipboard(csv);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
    setIsOpen(false);
  };

  const handleCopyJSON = async () => {
    const json = JSON.stringify(exportableData, null, 2);
    const success = await copyToClipboard(json);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
    setIsOpen(false);
  };

  if (!hasData) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-grey-medium hover:text-black transition-colors"
        aria-label="Export data"
      >
        {/* Download icon (Solar style) */}
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 3v12m0 0l-4-4m4 4l4-4M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2"
          />
        </svg>
        {copySuccess ? 'Copied!' : 'Export'}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-grey-medium/30 shadow-sm z-50">
          <div className="py-1">
            <button
              onClick={handleCSVExport}
              className="w-full px-4 py-2 text-left text-sm font-light text-black hover:bg-grey-light transition-colors"
            >
              Download CSV
            </button>
            <button
              onClick={handleJSONExport}
              className="w-full px-4 py-2 text-left text-sm font-light text-black hover:bg-grey-light transition-colors"
            >
              Download JSON
            </button>
            <div className="border-t border-grey-medium/20 my-1" />
            <button
              onClick={handleCopyCSV}
              className="w-full px-4 py-2 text-left text-sm font-light text-black hover:bg-grey-light transition-colors"
            >
              Copy as CSV
            </button>
            <button
              onClick={handleCopyJSON}
              className="w-full px-4 py-2 text-left text-sm font-light text-black hover:bg-grey-light transition-colors"
            >
              Copy as JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
